// The chrome's view of the session, as the object it has always read.
//
// **The decisions moved out** (PLAN-CLIENT-SPLIT day 5): what a click means
// is `src/engine/session/Session.ts`, a plain class over the store. This hook
// is the adapter — one `useSyncExternalStore` over the session's revision,
// the two inputs the client owns pushed in, the two outputs the client owns
// (sound, a pulse on the board) pulled out — and it hands back the session's
// `interaction()`: the old hook's fifty keys, unchanged, so every zone reads
// what it read yesterday until day 8 moves each onto `SessionView`.

import { useEffect, useMemo, useSyncExternalStore } from "react";

import { sound } from "src/client/ai/audio/engine";
import type { Interaction } from "src/engine/session/Session";
import type { Color } from "src/shared/board";
import { PIECE_TYPE, STRUCTURE_KIND } from "src/client/ai/render/factNames";
import { pieceVoiceOf, SELECT_VOICE, structureVoiceOf } from "./soundMap";
import { useGame } from "./useGame";

/**
 * Click-to-select, click-to-move — the session's, read through React.
 *
 * `seatSide` is the one side this client is allowed to play, or null when it
 * plays both (hot-seat); `building` is whether the Build tool is up, the one
 * click that does not infer from context. Both are the client's to know and
 * the session's to act on, so they are pushed in rather than read.
 */
export function useBoardInteraction(
  onAction?: (square: number) => void,
  seatSide?: Color | null,
  building = false
) {
  const game = useGame();
  const { session } = game;

  useEffect(() => session.setSeat(seatSide), [session, seatSide]);
  useEffect(() => session.setBuilding(building), [session, building]);

  // **The two client outputs.** A piece says its name when selected and a
  // card sounds when it goes; a square pulses when a chip fires. Both are
  // moments the board does not show, said by the session as events.
  useEffect(
    () =>
      game.on("cue", (cue) => {
        if (cue.kind === "card-played") sound.playUi("card-play");
        else if ("piece" in cue) {
          const voice = pieceVoiceOf(PIECE_TYPE[cue.piece]);
          if (voice) sound.playUi(voice, { shape: SELECT_VOICE });
        } else {
          const voice = structureVoiceOf(STRUCTURE_KIND[cue.structure]);
          if (voice) sound.playUi(voice, { shape: SELECT_VOICE });
        }
      }),
    [game]
  );
  useEffect(
    () =>
      game.on("focus", (focus) => {
        if (focus.kind === "pulse") onAction?.(focus.square);
      }),
    [game, onAction]
  );

  const revision = useSyncExternalStore(
    session.subscribe,
    () => session.revision,
    () => session.revision
  );
  // Rebuilt per revision — the same cadence the old hook rebuilt it on.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `revision` is the change signal
  return useMemo(() => session.interaction(), [session, revision]);
}

/**
 * What a click means, as the shell's zones take it.
 *
 * **One prop, one hop** (PLAN-UI §28.9): this object is rebuilt every
 * revision, so a zone that takes it can never be `React.memo`'d and nothing
 * threads it past the zone that reads it.
 */
export type BoardInteraction = Interaction;
